import { put, takeLatest } from "redux-saga/effects";
import { httpDelete, httpGet, httpPost, httpPut } from "../../services";
import { Toastify } from "../../components/Toaster";

import { PayloadAction } from "@reduxjs/toolkit";
import {
  addBrandFailure,
  addBrandFromAIFailure,
  addBrandFromAIRequest,
  addBrandFromAISuccess,
  addBrandFromTemplateFailure,
  addBrandFromTemplateRequest,
  addBrandFromTemplateSuccess,
  addBrandRequest,
  addBrandSuccess,
  addStyleFailure,
  addStyleRequest,
  addStyleSuccess,
  addTranslationFailure,
  addTranslationRequest,
  addTranslationSuccess,
  deleteBrandFailure,
  deleteBrandRequest,
  deleteBrandSuccess,
  deleteStyleFailure,
  deleteStyleRequest,
  deleteStyleSuccess,
  getBrandsFailure,
  getBrandsRequest,
  getBrandsSuccess,
  getCurrentBrandFailure,
  getCurrentBrandRequest,
  getCurrentBrandSuccess,
  removeTranslationFailure,
  removeTranslationRequest,
  removeTranslationSuccess,
  setDefaultBrandFailure,
  setDefaultBrandRequest,
  setDefaultBrandSuccess,
  setDefaultStyleFailure,
  setDefaultStyleRequest,
  setDefaultStyleSuccess,
  updateAllBrandContentFailure,
  updateAllBrandContentRequest,
  updateAllBrandContentSuccess,
  updateBrandFailure,
  updateBrandRequest,
  updateBrandSuccess,
  updateCurrentBrandFailure,
  updateCurrentBrandRequest,
  updateCurrentBrandSuccess,
  updateStyleFailure,
  updateStyleRequest,
  updateStyleSuccess,
  updateTranslationFailure,
  updateTranslationRequest,
  updateTranslationSuccess
} from "../features/hostedPages/hostedpages";

import {
  addPageFailure,
  addPageRequest,
  addPageSuccess,
  deletePageRequest,
  deletePageSuccess,
  deletePageFailure,
  AddPagePayload,
  UpdatePagePayload,
  DeletePagePayload,
  GetPagesPayload,
  updateV2PageRequest,
  deleteV2PageRequest,
  getPagesFailure,
  getPagesRequest,
  getPagesSuccess,
  getV2PagesRequest,
  resetV2PagesRequest,
  updatePageFailure,
  updatePageRequest,
  updatePageSuccess,
  resetPagesRequest,
  ResetPagesPayload
} from "../features/hostedPages/pages";

// --- Brands Sagas ---

function* fetchBrands() {
  try {
    let response = yield httpGet("/hosted-pages/brands", {});
    if (response.ErrorCode) {
      if (response.ErrorCode === 441) {
        // If feature not enabled need to return empty list
        response = [];
      } else {
        throw new Error(response.Description || response.Message || response.ErrorMessage);
      }
    }
    yield put(getBrandsSuccess(response));
  } catch (error) {
    yield put(getBrandsFailure(error.message));
  }
}

function* addBrand(action) {
  try {
    const response = yield httpPost("/hosted-pages/brands", {}, action.payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(addBrandSuccess(response));
  } catch (error) {
    yield put(addBrandFailure(error.message));
  }
}

function* addBrandFromTemplate(action) {
  try {
    const response = yield httpPost("/hosted-pages/brands/template", {}, action.payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(addBrandFromTemplateSuccess(response));
  } catch (error) {
    yield put(addBrandFromTemplateFailure(error.message));
  }
}
function* addBrandFromAI(action) {
  try {
    const response = yield httpPost("/hosted-pages/brands/ai", {}, action.payload);
    if (response.errorCode || response.ErrorCode) {
      const error = {
        message: response.error || response.Description || response.Message || response.ErrorMessage,
        errorCode: response.errorCode || response.ErrorCode
      };
      throw error;
    }
    yield put(addBrandFromAISuccess(response.brandData));
  } catch (error) {
    yield put(
      addBrandFromAIFailure({
        error: error.message,
        errorCode: error.errorCode
      })
    );
  }
}

function* updateBrand(action) {
  const { id, ...data } = action.payload;
  try {
    const response = yield httpPut(`/hosted-pages/brands/${id}`, {}, data);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updateBrandSuccess(response));
  } catch (error) {
    yield put(updateBrandFailure(error.message));
  }
}

function* deleteBrand(action) {
  const id = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/brands/${id}`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.IsDeleted) {
      yield put(deleteBrandSuccess(id));
    } else {
      yield put(deleteBrandFailure("Template not found"));
    }
  } catch (error) {
    yield put(deleteBrandFailure(error.message));
  }
}

function* setDefaultBrand(action: PayloadAction<string>) {
  const brandId = action.payload;
  try {
    const response = yield httpPost(`/hosted-pages/brands/${brandId}/default`, {}, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(setDefaultBrandSuccess(brandId));
  } catch (error) {
    yield put(setDefaultBrandFailure(error.message));
  }
}

// --- Current Brand Sagas ---

function* fetchCurrentBrand(action) {
  const brandId = action.payload;
  try {
    const response = yield httpGet(`/hosted-pages/brands/${brandId}`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getCurrentBrandSuccess(response));
  } catch (error) {
    yield put(getCurrentBrandFailure(error.message));
  }
}

function* updateCurrentBrand(action) {
  const { id, ...data } = action.payload;
  try {
    const response = yield httpPut(`/hosted-pages/brands/${id}`, {}, data);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updateCurrentBrandSuccess(response));
  } catch (error) {
    yield put(updateCurrentBrandFailure(error.message));
  }
}
function* updateTranslationContent(action: PayloadAction<{ brandId: any; locale: string; component: string; content: any }>) {
  const { brandId, locale, component, content } = action.payload;
  try {
    const response = yield httpPut(
      `/hosted-pages/brands/${brandId}/translation/${locale}`,
      {},
      {
        component,
        content
      }
    );
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updateTranslationSuccess(response));
  } catch (error) {
    yield put(updateTranslationFailure(error.message));
  }
}

function* addTranslationContent(action: PayloadAction<{ brandId: any; locale: string }>) {
  const { brandId, locale } = action.payload;
  try {
    const response = yield httpPost(`/hosted-pages/brands/${brandId}/translation`, {}, { locale });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(addTranslationSuccess(response));
  } catch (error) {
    yield put(addTranslationFailure(error.message));
  }
}

function* removeTranslationContent(action: PayloadAction<{ brandId: any; locale: string }>) {
  const { brandId, locale } = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/brands/${brandId}/translation/${locale}`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.success === true) {
      yield put(removeTranslationSuccess({ brandId, locale }));
    } else {
      yield put(removeTranslationFailure("Translation not found"));
    }
  } catch (error) {
    yield put(removeTranslationFailure(error.message));
  }
}

// --- Styles Sagas ---

function* addStyle(action) {
  const { brandId, style } = action.payload;
  try {
    const response = yield httpPost(`/hosted-pages/brands/${brandId}/styles`, {}, style);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(addStyleSuccess(response));
  } catch (error) {
    yield put(addStyleFailure(error.message));
  }
}

function* updateStyle(action) {
  const { brandId, styleName, style } = action.payload;
  try {
    const response = yield httpPut(`/hosted-pages/brands/${brandId}/styles/${styleName}`, {}, style);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updateStyleSuccess(response));
  } catch (error) {
    yield put(updateStyleFailure(error.message));
  }
}

function* deleteStyle(action) {
  const { brandId, styleName } = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/brands/${brandId}/styles/${styleName}`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    if (response.message === "Style removed successfully") {
      yield put(deleteStyleSuccess(styleName));
    } else {
      yield put(deleteStyleFailure("Style not found"));
    }
  } catch (error) {
    yield put(deleteStyleFailure(error.message));
  }
}

function* setDefaultStyle(action: PayloadAction<{ brandId: string; styleName: string }>) {
  const { brandId, styleName } = action.payload;
  try {
    const response = yield httpPost(`/hosted-pages/brands/${brandId}/styles/${styleName}/default`, {}, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(setDefaultStyleSuccess(styleName));
  } catch (error) {
    yield put(setDefaultStyleFailure(error.message));
  }
}

function* updateAllBrandContent(action: PayloadAction<{ brandId: string; stylePayload: any | null; contentPayload: Record<string, any> | null }>) {
  const { brandId, stylePayload, contentPayload } = action.payload;

  if (stylePayload) {
    for (const [styleName, style] of Object.entries(stylePayload)) {
      try {
        const response = yield httpPut(`/hosted-pages/brands/${brandId}/styles/${styleName}`, {}, style);
        if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
        yield put(updateStyleSuccess({ ...response, showToast: Object.entries(stylePayload).length > 1 ? false : true }));
      } catch (error) {
        yield put(updateAllBrandContentFailure(error.message));
      }
    }
  }

  if (contentPayload) {
    for (const [locale, content] of Object.entries(contentPayload)) {
      for (const [component, compContent] of Object.entries(content)) {
        try {
          const response = yield httpPut(
            `/hosted-pages/brands/${brandId}/translation/${locale}`,
            {},
            {
              component,
              content: compContent
            }
          );
          if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
          yield put(updateTranslationSuccess({ ...response, showToast: Object.entries(contentPayload).length > 1 ? false : true }));
        } catch (error) {
          yield put(updateAllBrandContentFailure(error.message));
        }
      }
    }
  }
  yield put(updateAllBrandContentSuccess({ success: true }));
}
// --- Watchers ---

export function* watchHostedPagesSaga() {
  yield takeLatest(updateAllBrandContentRequest.type, updateAllBrandContent);

  yield takeLatest(getBrandsRequest.type, fetchBrands);
  yield takeLatest(addBrandRequest.type, addBrand);
  yield takeLatest(addBrandFromTemplateRequest.type, addBrandFromTemplate);
  yield takeLatest(addBrandFromAIRequest.type, addBrandFromAI);
  yield takeLatest(updateBrandRequest.type, updateBrand);
  yield takeLatest(deleteBrandRequest.type, deleteBrand);
  yield takeLatest(setDefaultBrandRequest.type, setDefaultBrand);
  yield takeLatest(setDefaultStyleRequest.type, setDefaultStyle);
  yield takeLatest(getCurrentBrandRequest.type, fetchCurrentBrand);
  yield takeLatest(updateCurrentBrandRequest.type, updateCurrentBrand);

  yield takeLatest(addTranslationRequest.type, addTranslationContent);
  yield takeLatest(updateTranslationRequest.type, updateTranslationContent);
  yield takeLatest(removeTranslationRequest.type, removeTranslationContent);

  yield takeLatest(addStyleRequest.type, addStyle);
  yield takeLatest(updateStyleRequest.type, updateStyle);
  yield takeLatest(deleteStyleRequest.type, deleteStyle);
}

// --- Brand Pages Sagas ---

function* fetchPages(action: PayloadAction<GetPagesPayload>) {
  const { brandId } = action.payload;
  try {
    const response = yield httpGet(`/hosted-pages/pages/${brandId}`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getPagesSuccess({ data: { ...response, BrandId: brandId } }));
  } catch (error) {
    yield put(getPagesFailure(error.message));
  }
}

function* fetchV2Pages() {
  try {
    const response = yield httpGet(`/hosted-pages/v2/pages`, {});
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getPagesSuccess({ data: response }));
  } catch (error) {
    yield put(getPagesFailure(error.message));
  }
}

function* addPage(action: PayloadAction<AddPagePayload>) {
  const { BrandId, ...data } = action.payload;
  try {
    const response = yield httpPost(`/hosted-pages/brands/${BrandId}/pages`, {}, data);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(addPageSuccess(response));
  } catch (error) {
    yield put(addPageFailure(error.message));
  }
}

function* updatePage(action: PayloadAction<UpdatePagePayload>) {
  const { BrandId, Files } = action.payload;
  try {
    const response = yield httpPut(`/hosted-pages/page/${BrandId}`, {}, Files);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updatePageSuccess(response));
  } catch (error) {
    yield put(updatePageFailure(error.message));
  }
}

function* updateV2Page(action: PayloadAction<UpdatePagePayload>) {
  const { Files } = action.payload;
  try {
    const response = yield httpPut(`/hosted-pages/v2/pages`, {}, Files);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(updatePageSuccess(response));
  } catch (error) {
    yield put(updatePageFailure(error.message));
  }
}

function* deletePage(action: PayloadAction<DeletePagePayload>) {
  const { BrandId, pageType, ...payload } = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/brands/${BrandId}/page/${pageType}`, {}, payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(deletePageSuccess(response));
    const label = action.payload.section === "HeadTags" ? "Head" : "Page";
    Toastify("success", `${label} deleted successfully`);
  } catch (error) {
    yield put(deletePageFailure(error.message));
  }
}

function* deleteV2Page(action: PayloadAction<DeletePagePayload>) {
  const { pageType, ...payload } = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/v2/page/${pageType}`, {}, payload);
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(deletePageSuccess(response));
    const label = action.payload.section === "HeadTags" ? "Head" : "Page";
    Toastify("success", `${label} deleted successfully`);
  } catch (error) {
    yield put(deletePageFailure(error.message));
  }
}
function* resetV2Pages(action: PayloadAction<string[]>) {
  const pages = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/v2/pages/reset`, {}, { pages: pages });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getPagesSuccess({ data: response, isReset: true }));
  } catch (error) {
    yield put(getPagesFailure(error.message));
  }
}
function* resetPages(action: PayloadAction<ResetPagesPayload>) {
  const { pages, brandId } = action.payload;
  try {
    const response = yield httpDelete(`/hosted-pages/brands/${brandId}/pages/reset`, {}, { pages: pages });
    if (response.ErrorCode) throw new Error(response.Description || response.Message || response.ErrorMessage);
    yield put(getPagesSuccess({ data: response, isReset: true }));
  } catch (error) {
    yield put(getPagesFailure(error.message));
  }
}

// --- Watcher for Brand Pages ---

export function* watchBrandPagesSaga() {
  yield takeLatest(getV2PagesRequest.type, fetchV2Pages);
  yield takeLatest(getPagesRequest.type, fetchPages);
  yield takeLatest(addPageRequest.type, addPage);
  yield takeLatest(updatePageRequest.type, updatePage);
  yield takeLatest(updateV2PageRequest.type, updateV2Page);
  yield takeLatest(deletePageRequest.type, deletePage);
  yield takeLatest(deleteV2PageRequest.type, deleteV2Page);
  yield takeLatest(resetV2PagesRequest.type, resetV2Pages);
  yield takeLatest(resetPagesRequest.type, resetPages);
}
